import { useState } from "react";
import axios from "axios";
import { Terminal, LogOut, Loader2, LayoutDashboard, Inbox, School, Wrench, Sparkles, Mail } from "lucide-react";
import Dashboard from "@/components/admin/Dashboard";
import Leads from "@/components/admin/Leads";
import Schools from "@/components/admin/Schools";
import Tickets from "@/components/admin/Tickets";
import Agent from "@/components/admin/Agent";
import Outreach from "@/components/admin/Outreach";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "schools", label: "Schools", icon: School },
  { id: "tickets", label: "Tickets", icon: Wrench },
  { id: "outreach", label: "Outreach", icon: Mail },
  { id: "agent", label: "AI Agent", icon: Sparkles },
];

function formatDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("gl_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("dashboard");

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("gl_admin_token", data.token);
      setToken(data.token);
    } catch (e2) {
      setError(formatDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("gl_admin_token");
    setToken("");
  };

  const inputCls =
    "w-full rounded-none border-b border-[#DCD6CE] bg-transparent px-0 py-3 text-[#141210] placeholder:text-[#8A837B] focus:border-neon-teal focus:outline-none transition-colors duration-300";

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-ink px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md border border-[#E6E2DC] bg-white p-10"
        >
          <div className="mb-8 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center border border-neon-teal/60 bg-[#581820]/[0.05] text-neon-teal">
              <Terminal size={20} />
            </span>
            <div>
              <h1 className="font-display text-xl font-bold uppercase tracking-tight text-[#141210]">
                The Geeks Labs Admin
              </h1>
              <p className="text-xs text-[#6B655F]">Sign in to the ops console</p>
            </div>
          </div>
          <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
            Email
          </label>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@geeklabsinc.com"
            className={inputCls}
          />
          <label className="mb-2 mt-6 block text-[10px] font-bold uppercase tracking-[0.25em] text-[#6B655F]">
            Password
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-2 bg-[#141210] px-6 py-3.5 text-xs font-bold uppercase tracking-[0.15em] text-[#FAF8F5] transition-colors duration-300 hover:bg-neon-teal disabled:opacity-60"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-console" className="min-h-screen bg-ink md:flex">
      <aside className="border-b border-[#E6E2DC] bg-white md:fixed md:left-0 md:top-0 md:flex md:h-screen md:w-60 md:flex-col md:border-b-0 md:border-r">
        <div className="flex items-center gap-3 px-5 py-5 md:border-b md:border-[#E6E2DC]">
          <span className="flex h-9 w-9 items-center justify-center border border-neon-teal/60 bg-[#581820]/[0.05] text-neon-teal">
            <Terminal size={17} />
          </span>
          <div>
            <span className="block font-display text-base font-bold uppercase tracking-tight text-[#141210]">
              The Geeks Labs
            </span>
            <span className="block text-[9px] font-bold uppercase tracking-[0.3em] text-[#8A837B]">
              Ops Console
            </span>
          </div>
        </div>
        <nav
          className="flex gap-1 overflow-x-auto px-3 pb-3 md:flex-1 md:flex-col md:gap-0.5 md:overflow-visible md:px-3 md:py-6"
          data-testid="console-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2.5 whitespace-nowrap px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors duration-300 md:border-l-2 ${
                tab === t.id
                  ? "bg-[#581820]/[0.06] text-[#581820] md:border-[#581820]"
                  : "text-[#8A837B] hover:text-[#141210] md:border-transparent"
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="hidden border-t border-[#E6E2DC] px-3 py-4 md:block">
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A837B] transition-colors duration-300 hover:text-[#581820]"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
        <button
          data-testid="admin-logout-button-mobile"
          onClick={logout}
          className="ml-auto mr-3 flex items-center gap-2 self-center px-3 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#8A837B] md:hidden"
        >
          <LogOut size={14} /> Logout
        </button>
      </aside>

      <div className="flex-1 px-4 py-6 md:ml-60 md:px-10 md:py-10">
        <div className="mx-auto max-w-[1200px]">
          {tab === "dashboard" && <Dashboard token={token} onAuthError={logout} />}
          {tab === "leads" && <Leads token={token} onAuthError={logout} />}
          {tab === "schools" && <Schools token={token} onAuthError={logout} />}
          {tab === "tickets" && <Tickets token={token} onAuthError={logout} />}
          {tab === "outreach" && <Outreach token={token} onAuthError={logout} />}
          {tab === "agent" && <Agent token={token} onAuthError={logout} />}
        </div>
      </div>
    </main>
  );
}
